// handlers/BatchTransformationHandler.ts
import {
  FireTVCampaignAutomationClient,
  TransformAssetInput,
  TransformationVariant,
  TransformationStatus,
  AssetType,
  Placement,
  GetTransformedAssetCommandOutput,
} from '@amzn/fire-tv-campaign-automation-service';
import { BatchTransformResult } from 'src/contexts/SmartUploadContext';
import { ToastContextType } from 'src/contexts/ToastContext';
import { logger } from 'src/logger';
import { AssetStatus } from 'src/types';
import { transformAsset, getTransformedAsset } from 'src/utils/clients/controlPlane/apiMethods';
import { poll } from 'src/utils/Poller';

const POLL_INTERVAL_SECONDS = 10;
const POLL_MAX_RETRIES = 18;
const POLL_TIMEOUT_SECONDS = 180;

const BATCH_VARIANTS: TransformationVariant[] = [
  TransformationVariant.FULL_BLEED,
  TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY,
];

const TERMINAL_STATES: TransformationStatus[] = [
  TransformationStatus.TRANSFORMATION_COMPLETE,
  TransformationStatus.TRANSFORMATION_FAILED,
];

export interface BatchTransformParams {
  sourceAssetKey: string;
  placement: Placement;
  actor: string;
  controlPlaneClient: FireTVCampaignAutomationClient;
  toast: ToastContextType;
  onResultUpdate: (variant: TransformationVariant, update: Partial<BatchTransformResult>) => void;
  abortSignal?: AbortSignal;
}

export async function executeBatchTransform(
  params: BatchTransformParams,
): Promise<BatchTransformResult[]> {
  const {
    sourceAssetKey,
    placement,
    actor,
    controlPlaneClient,
    toast,
    onResultUpdate,
    abortSignal,
  } = params;

  console.log('[IDEA] executeBatchTransform started', {
    sourceAssetKey,
    placement,
    variants: BATCH_VARIANTS,
  });

  const promises = BATCH_VARIANTS.map(async (variant): Promise<BatchTransformResult> => {
    console.log(`[IDEA] Starting transform for variant: ${variant}`);
    onResultUpdate(variant, { variant, status: AssetStatus.TRANSFORMATION_IN_PROGRESS });

    try {
      const input: TransformAssetInput = {
        assetType: AssetType.BACKGROUND_IMAGE,
        transformationVariant: variant,
        placement,
        actor,
        sourceAssetKey,
        topOffset: 0,
        leftOffset: 0,
      };

      console.log(`[IDEA] Calling transformAsset API for ${variant}`, input);
      const response = await transformAsset(controlPlaneClient, input);
      const transformationId = response.transformationId;
      console.log(
        `[IDEA] transformAsset response for ${variant}: transformationId=${transformationId}`,
      );
      onResultUpdate(variant, { transformationId });

      console.log(`[IDEA] Starting poll for ${variant} (id=${transformationId})`);
      const result = await poll(() => getTransformedAsset(controlPlaneClient, transformationId), {
        intervalSeconds: POLL_INTERVAL_SECONDS,
        maxRetries: POLL_MAX_RETRIES,
        timeoutSeconds: POLL_TIMEOUT_SECONDS,
        abortSignal,
        successCondition: (r: GetTransformedAssetCommandOutput) => {
          const isTerminal = TERMINAL_STATES.includes(r.status);
          console.log(
            `[IDEA] Poll check for ${variant}: status=${r.status}, terminal=${isTerminal}`,
          );
          return isTerminal;
        },
      });

      console.log(
        `[IDEA] Poll complete for ${variant}: status=${result.status}, key=${result.transformedAssetKey}`,
      );

      if (result.status === TransformationStatus.TRANSFORMATION_COMPLETE) {
        const transformedAssetKey = result.transformedAssetKey ?? undefined;
        onResultUpdate(variant, { status: AssetStatus.COMPLETED, transformedAssetKey });
        return { variant, transformationId, status: AssetStatus.COMPLETED, transformedAssetKey };
      }

      onResultUpdate(variant, { status: AssetStatus.FAILED, error: 'Transformation failed' });
      return {
        variant,
        transformationId,
        status: AssetStatus.FAILED,
        error: 'Transformation failed',
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error(`[IDEA] Batch transform FAILED for ${variant}:`, message, error);
      logger.error(`Batch transform failed for ${variant}`, { error });
      onResultUpdate(variant, { status: AssetStatus.FAILED, error: message });
      return { variant, status: AssetStatus.FAILED, error: message };
    }
  });

  const results = await Promise.all(promises);

  const failures = results.filter((r) => r.status === AssetStatus.FAILED);
  console.log(
    `[IDEA] All batch transforms done. Total=${results.length}, Failed=${failures.length}`,
    results,
  );

  if (failures.length === results.length) {
    toast.addToast({
      variant: 'danger',
      description: 'All transformations failed. Please try again.',
    });
  } else if (failures.length > 0) {
    toast.addToast({
      variant: 'warning',
      description: `${failures.length} of ${results.length} transformations failed.`,
    });
  }

  return results;
}
